import { useEffect, useState } from "react";
import { UserPlus, Copy, Check } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";

const STATUS_LABELS = { active: "Active", inactive: "Inactive", pending_invite: "Invite Sent" };

// Test Drive 2026-10-21 — a vendor manages its own team, like a contractor.
// Every vendor user is a Vendor Admin with full access.
export default function VendorTeam() {
  const { user } = useAuth();
  const [team, setTeam] = useState([]);
  const [show, setShow] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [inviteLink, setInviteLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/vendor/team").then((r) => setTeam(r.data));
  useEffect(() => { load(); }, []);

  const errorText = (e, fallback) => {
    const detail = e.response?.data?.detail;
    return Array.isArray(detail) ? detail.map((d) => d.msg || d.message).join(" ") : detail || fallback;
  };

  const invite = async () => {
    if (!name.trim() || !email.trim()) return toast.error("Name and email required");
    setBusy(true);
    try {
      const { data } = await api.post("/vendor/team", { name, email });
      setInviteLink(`${window.location.origin}/accept-invite?token=${data.invite_token}`);
      setName(""); setEmail("");
      load();
    } catch (e) {
      toast.error(errorText(e, "Failed to invite"));
    } finally {
      setBusy(false);
    }
  };

  const resend = async (member) => {
    try {
      const { data } = await api.post(`/vendor/team/${member.id}/resend-invite`);
      setInviteLink(`${window.location.origin}/accept-invite?token=${data.invite_token}`);
      setShow(true);
      toast.success(`New invite sent to ${member.email}`);
    } catch (e) {
      toast.error(errorText(e, "Failed to resend the invite"));
    }
  };

  const toggleStatus = async (member) => {
    try {
      await api.patch(`/vendor/team/${member.id}`, { status: member.status === "active" ? "inactive" : "active" });
      load();
    } catch (e) {
      toast.error(errorText(e, "Failed to update"));
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      toast.success("Invite link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Couldn't access the clipboard — select and copy the link manually");
    }
  };

  const closeInviteModal = () => { setShow(false); setInviteLink(null); setCopied(false); };

  return (
    <div className="p-6 md:p-8" data-testid="vendor-team-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">Team</h1>
          <p className="text-slate-500 text-sm mt-1">Everyone on your team has full access to your vendor portal.</p>
        </div>
        <button onClick={() => setShow(true)} data-testid="vendor-invite-member-button" className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-semibold">
          <UserPlus size={14} /> Invite Member
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full mt-7 text-[13px]" data-testid="vendor-team-table">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold border-b border-slate-100">
              <th className="text-left py-2.5">Name</th><th className="text-left py-2.5">Email</th><th className="text-left py-2.5">Role</th><th className="text-left py-2.5">Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {team.map((m) => (
              <tr key={m.id} className="border-b border-slate-50" data-testid={`vendor-team-row-${m.id}`}>
                <td className="py-3 font-medium">{m.name}{m.id === user?.id && <span className="ml-1.5 text-[11px] text-slate-400">(you)</span>}</td>
                <td className="py-3 text-slate-500">{m.email}</td>
                <td className="py-3">Vendor Admin</td>
                <td className="py-3">{STATUS_LABELS[m.status] || m.status}</td>
                <td className="py-3 text-right whitespace-nowrap">
                  {m.status === "pending_invite" ? (
                    <button onClick={() => resend(m)} data-testid={`vendor-resend-invite-${m.id}`} className="text-[12px] font-semibold text-sky-600">Resend invite</button>
                  ) : m.id !== user?.id && (
                    <button onClick={() => toggleStatus(m)} data-testid={`vendor-toggle-status-${m.id}`} className="text-[12px] font-semibold text-sky-600">
                      {m.status === "active" ? "Deactivate" : "Activate"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {team.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-slate-400 text-sm">No team members yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={show} onOpenChange={(v) => (v ? setShow(true) : closeInviteModal())}>
        <DialogContent data-testid="vendor-invite-modal">
          <DialogHeader><DialogTitle>{inviteLink ? "Invite Sent" : "Invite Team Member"}</DialogTitle></DialogHeader>
          {inviteLink ? (
            <div className="space-y-3 mt-2">
              <p className="text-[12.5px] text-slate-500">We emailed the invite. You can also share this link — it works once and expires in 7 days.</p>
              <div className="flex gap-2">
                <input readOnly value={inviteLink} data-testid="vendor-invite-link" className="flex-1 h-10 px-3 rounded-lg border border-slate-200 text-[12px] mono" onFocus={(e) => e.target.select()} />
                <button onClick={copyLink} className="px-3 h-10 rounded-lg border border-slate-200 text-[12.5px] font-semibold flex items-center gap-1.5">
                  {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy"}
                </button>
              </div>
              <button onClick={closeInviteModal} className="w-full h-10 rounded-lg bg-slate-900 text-white text-[13px] font-semibold">Done</button>
            </div>
          ) : (
            <div className="space-y-3 mt-2">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" data-testid="vendor-invite-name-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
              <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Email" data-testid="vendor-invite-email-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
              <p className="text-[11.5px] text-slate-400">They'll join as a Vendor Admin with full access.</p>
              <button disabled={busy} onClick={invite} data-testid="vendor-send-invite-button" className="w-full h-10 rounded-lg bg-sky-500 text-white text-[13px] font-semibold disabled:opacity-60">
                {busy ? "Sending…" : "Send Invite"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
